import { uuidModule } from "@/modules/uuid";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { NoteModelData, NoteUpsertedSyncUpdateValue } from "@/store/note/types";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import {
  buildSuggestionStatus,
  getRenderableSuggestionData,
  NoteSuggestionStatus,
} from "@/store/note/suggestion-data";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IAcceptNoteSuggestionOperation } from "@/store/sync/operations/types";
import {
  SyncModelData,
  OptimisticSyncUpdate,
  SyncOperationKind,
  SyncUpdateValue,
} from "@/store/sync/types";

type AcceptNoteSuggestionOptimisticData = {
  note: SyncUpdateValue<NoteModelData>;
  spaceAccountNote: SyncUpdateValue<SpaceAccountNoteModelData>;
};

export class AcceptNoteSuggestionOperation extends BaseSyncOperation<IAcceptNoteSuggestionOperation> {
  private optimisticData: AcceptNoteSuggestionOptimisticData;

  get operationKind(): SyncOperationKind {
    return "ACCEPT_NOTE_SUGGESTION";
  }

  constructor({
    optimisticData,
    ...params
  }: BaseSyncOperationParams<IAcceptNoteSuggestionOperation> & {
    optimisticData: AcceptNoteSuggestionOptimisticData;
  }) {
    super(params);
    this.optimisticData = optimisticData;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const note = this.optimisticData.note;
    const spaceAccountNote = this.optimisticData.spaceAccountNote;
    const suggestionData = getRenderableSuggestionData({
      suggestionData: spaceAccountNote.model_data.suggestion_data,
    });
    if (!suggestionData) return [];

    const noteValue: NoteUpsertedSyncUpdateValue = {
      model_id: note.model_id,
      model_kind: "NOTE",
      model_version: note.model_version,
      model_data: {
        ...note.model_data,
        primary_label: suggestionData.suggested_primary_label,
        secondary_label: suggestionData.suggested_secondary_label,
      },
      model_scopes: note.model_scopes,
    };
    const noteUpserted: OptimisticSyncUpdate<NoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteValue,
    };

    const spaceAccountNoteUpsertedValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNote.model_id,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNote.model_version ?? 0,
      model_data: {
        ...spaceAccountNote.model_data,
        suggestion_data: buildSuggestionStatus({
          suggestionData,
          status: NoteSuggestionStatus.Accepted,
        }),
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const spaceAccountNoteUpserted: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteUpsertedValue,
    };

    return [noteUpserted, spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
    await this.store.notes.recompute(this.payload.note_id);
    const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
      noteId: this.payload.note_id,
    });
    await this.store.noteContentDocuments.recompute(noteContentDocumentId);
  }
}
